/**
 * BIRHAUS Common Types & Utilities
 * 
 * Relaxed type definitions that maintain backward compatibility
 * while allowing more flexible usage patterns.
 */

// Flexible locale type that accepts common patterns
export type BirhausLocale = 'es' | 'es-PY' | 'es-ES' | 'en' | 'en-US' | string

// Base props that all BIRHAUS components can extend
export interface BaseBirhausProps {
  locale?: BirhausLocale
  className?: string
  'data-testid'?: string
}

// Enhanced component props utility
export type BirhausComponentProps<T> = T & BaseBirhausProps

// Variant types for consistency
export type BirhausVariant = 'primary' | 'secondary' | 'destructive' | 'outline' | 'ghost'
export type BirhausSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'
export type BirhausInputVariant = 'default' | 'filled' | 'minimal'
export type BirhausCardVariant = 'default' | 'outline' | 'filled' | 'elevated'
export type BirhausBadgeVariant = 'default' | 'success' | 'warning' | 'error' | 'info'

// Spanish-first label props pattern
export interface BirhausLabelProps {
  label?: string
  labelEs?: string
  labelEn?: string
}

// Spanish-first description props pattern
export interface BirhausDescriptionProps {
  description?: string
  descriptionEs?: string
  descriptionEn?: string
}

// Combined bilingual props
export interface BirhausBilingualProps extends BirhausLabelProps, BirhausDescriptionProps {
  helpText?: string
  helpTextEs?: string
  helpTextEn?: string
  error?: string
  errorEs?: string
  errorEn?: string
}

// Cognitive load tracking props
export interface CognitiveLoadProps {
  trackCognitiveLoad?: boolean
  cognitiveLoadLevel?: 'low' | 'medium' | 'high'
  cognitiveWeight?: number
}

// BIRHAUS compliance props
export interface BirhausComplianceProps {
  compliance?: string
  auditTrail?: boolean
  undoAction?: () => void | Promise<void>
  undoTimeoutMs?: number
}

// Common form field props
export interface BirhausFieldProps extends BirhausBilingualProps, CognitiveLoadProps {
  required?: boolean
  disabled?: boolean
  readOnly?: boolean
  fieldPriority?: 'required' | 'optional' | 'progressive'
}

// Data table/list item generic
export interface BirhausDataItem {
  id: string | number
  [key: string]: any
}

// Generic column definition
export interface BirhausColumn<T = BirhausDataItem> {
  key: keyof T
  label: string
  labelEs?: string
  labelEn?: string
  sortable?: boolean
  filterable?: boolean
  width?: string | number
  render?: (value: any, item: T) => React.ReactNode
}

// Bulk action definition
export interface BirhausBulkAction<T = BirhausDataItem> {
  id: string
  label: string
  labelEs?: string
  labelEn?: string
  icon?: React.ReactNode
  variant?: 'default' | 'destructive'
  needsConfirmation?: boolean
  undoable?: boolean
  handler: (items: T[]) => Promise<void> | void
}

// Pagination info
export interface BirhausPaginationInfo {
  page: number
  pageSize: number
  total: number
  totalPages: number
}

// Search/filter state
export interface BirhausFilterState {
  search?: string
  filters?: Record<string, any>
  sort?: {
    field: string
    direction: 'asc' | 'desc'
  }
}

// Toast data (flexible locale)
export interface BirhausToastData {
  id: string
  variant: 'success' | 'error' | 'warning' | 'info'
  title?: string
  titleEs?: string
  titleEn?: string
  message?: string
  messageEs?: string
  messageEn?: string
  duration?: number
  persistent?: boolean
  undoAction?: () => void | Promise<void>
  undoLabel?: string
  undoLabelEs?: string
  undoLabelEn?: string
  action?: {
    label: string
    labelEs?: string
    labelEn?: string
    onClick: () => void
  }
  onDismiss?: (id: string) => void
  locale?: BirhausLocale
}

// Status types (commonly used)
export type BirhausStatus = 
  | 'active' 
  | 'inactive' 
  | 'pending' 
  | 'approved' 
  | 'rejected' 
  | 'draft' 
  | 'published'
  | 'archived'
  | 'suspended'

// Financial status types (for BIRHAUS Financial package compatibility)
export type BirhausPaymentStatus = 
  | 'pending'
  | 'processing' 
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'refunded'

// Student status types (for educational contexts)
export type BirhausStudentStatus = 
  | 'enrolled'
  | 'graduated'
  | 'dropped'
  | 'suspended'
  | 'transferred'
  | 'inactive'

// Utility functions for locale handling
export const LocaleUtils = {
  /**
   * Normalizes locale to standard format
   */
  normalize(locale?: string): BirhausLocale {
    if (!locale) return 'es'
    
    const normalized = locale.toLowerCase()
    if (normalized.startsWith('es')) return 'es'
    if (normalized.startsWith('en')) return 'en'
    
    return locale as BirhausLocale
  },

  /**
   * Gets the appropriate text based on locale preference
   */
  getText(
    options: { es?: string; en?: string; default?: string },
    locale?: BirhausLocale
  ): string {
    const normalizedLocale = this.normalize(locale)
    
    if (normalizedLocale.startsWith('en') && options.en) {
      return options.en
    }
    
    return options.es || options.default || options.en || ''
  },

  /**
   * Type guard to check if locale is Spanish variant
   */
  isSpanish(locale?: BirhausLocale): boolean {
    return this.normalize(locale).startsWith('es')
  },

  /**
   * Type guard to check if locale is English variant
   */
  isEnglish(locale?: BirhausLocale): boolean {
    return this.normalize(locale).startsWith('en')
  }
}

// Generic validation rule
export interface BirhausValidationRule {
  test: (value: any) => boolean
  errorEs: string
  errorEn?: string
  solutionEs?: string
  solutionEn?: string
  severity?: 'error' | 'warning' | 'info'
}

// Generic options pattern
export interface BirhausOption<T = string> {
  value: T
  label: string
  labelEs?: string
  labelEn?: string
  description?: string
  descriptionEs?: string
  descriptionEn?: string
  disabled?: boolean
  icon?: React.ReactNode
  group?: string
}

// Generic group pattern
export interface BirhausOptionGroup<T = string> {
  id: string
  label: string
  labelEs?: string
  labelEn?: string
  options: BirhausOption<T>[]
  collapsible?: boolean
  defaultExpanded?: boolean
}

// Export utility types for common patterns
export type BirhausSelectOption<T = string> = BirhausOption<T>
export type BirhausSelectGroup<T = string> = BirhausOptionGroup<T>
export type BirhausComboboxOption<T = string> = BirhausOption<T>
export type BirhausRadioOption<T = string> = BirhausOption<T>
export type BirhausCheckboxOption<T = string> = BirhausOption<T> & { checked?: boolean }

// Re-export React types that are commonly needed
export type { ReactNode, ReactElement, ComponentProps, ForwardRefExoticComponent } from 'react'